import React from "react";
import { styled, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { DropdownSectionData } from "./types";
import { Section } from "./Section";

const EmptyMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 4),
  color: theme.palette.text.disabled,
  fontSize: theme.typography.body1.fontSize,
}));

interface Props {
  sections: DropdownSectionData[];
  isFetching: boolean;
  searchQuery: string;
  selectedValue: string | number;
  onSelect: (value: string | number) => void;
  highlightedIndex: number;
}

export const SectionsList = ({
  sections,
  isFetching,
  searchQuery,
  selectedValue,
  onSelect,
  highlightedIndex,
}: Props) => {
  const { t } = useTranslation();

  if (!sections.length && !isFetching) {
    return (
      <EmptyMessage>
        {searchQuery
          ? t("dropdownWithSections.noMatches")
          : t("dropdownWithSections.noOptions")}
      </EmptyMessage>
    );
  }

  if (isFetching && !sections.length) {
    return <EmptyMessage>{t("dropdownWithSections.loading")}</EmptyMessage>;
  }

  let cumulativeIndex = 0;

  return (
    <>
      {sections.map((section, sectionIndex) => {
        const startIndex = cumulativeIndex;
        const isLastSection = sectionIndex === sections.length - 1;
        cumulativeIndex += section.options.length;

        return (
          <Section
            key={`section-${section.label}-${sectionIndex}`}
            data-testid={`dropdown-section-${section.testId ?? section.label}`}
            searchQuery={searchQuery}
            section={section}
            hasMultipleSections={sections.length > 1}
            isLastSection={isLastSection}
            selectedValue={selectedValue}
            onSelect={onSelect}
            highlightedIndex={highlightedIndex}
            startIndex={startIndex}
          />
        );
      })}
    </>
  );
};
